import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import { uuid } from 'uuidv4';
import { PageActions, TextInput } from '../../../../components/form-elements/_index';
import { CountryInput } from '../../../../components/form-elements/CountryInput';
import { TimeZoneInput } from '../../../../components/form-elements/TimeZoneInput';
import { PageActionHeading, PageHeading } from '../../../../components/page-components/_index';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { hasPermission } from '../../../../containers/authorization-helpers/has-permission';
import { WeekDays, WeekDaysMetadata } from '../../../../models/enums/week-days';
import { DeliveryHourView, ShipToAddressRead } from '../../../../shared/models/ship-to-address-read';
import { TransportationSvc } from '../../../../transportation/service';
import { StatusCode } from '../../../../utils/api';
import { AppUtils } from '../../../../utils/app-utils';
import { history, navigateToPage } from '../../../../utils/history';
import { ModelValidator } from '../../../../utils/model-validator';
import { AddressDisplay } from '../../../../support/components/dealers/AddressDisplay';
import { AddressEntryDisplay } from '../../../../support/components/dealers/AddressEntryDisplay';
import { EditAddressHoursControl } from '../../../../support/components/dealers/EditAddressHoursControl';
import { DeliveryAddressUpdate, DeliveryHourUpdate } from '../../../../support/models/dealers/delivery-address-update';
import { DeliveryAddressCreate, DeliveryHourCreate, AddressCreateStatus, CreateAddress } from '../../../../support/models/dealers/delivery-address-create';
import { PageLevelError } from '../../../../components/form-elements/PageLevelError';
import { ToggleInput, TextArea } from '../../../../components/form-elements/_index';
import ResultDialog from '../../../../components/dialog-types/ResultDialog';
import YesNoCancelDialog from '../../../../components/dialog-types/YesNoCancelDialog';
import { showSuggestedAddressConfirmation } from '../../../../components/dialog-types/SuggestedAddressConfirmation';
import '../../../../support/styles.scss';
import { AddressDeliveryType, AddressDeliveryTypeMetadata, AddressDeliveryTypeSelectList } from '../../../../models/transportation/enums/addressDeliveryType';
import { SelectListItem } from '../../../../models/utilities/selectlist-item';

interface IProps {
    location    : any;
    match       : any;
}

interface IGroupedHours {
    id      : string;
    items   : DeliveryHourView[];
    days    : number; // actually  flags enum
}

export const EditDeliveryAddress = (props : IProps) => {

    const IS_DEBUG = false;

    const [editAddress, setEditAddress]             = React.useState(null as unknown as ShipToAddressRead);
    const [groupedHours, setGroupedHours]           = React.useState<IGroupedHours[]>([]);        
    const [hoursByControl, setHoursByControl]       = React.useState({});
    const [displayHours, setDisplayHours]           = React.useState<any>({});
    const [fixedRangeMode, setFixedRangeMode]       = React.useState(false);
    const [errorMsgs, setErrorMsgs]                 = React.useState({} as any);
    const [canEdit, setCanEdit]                     = React.useState(false);
    const [isNewAddressEntry, setIsNewAddressEntry] = React.useState(false);
    const [pageErrorMsg, setPageErrorMsg] = React.useState('');
    const [newDeliveryType, setNewDeliveryType] = React.useState<AddressDeliveryType | undefined>(undefined);
    const [deliveryTypes, setDeliveryTypes] = React.useState<Array<SelectListItem<string>>>([]);

    /**
     * Fetch the address if it's not in the route state
     * @param dealerId The dealer ID to get addresses for
     * @param addressId The address ID to pull from the returned list
     */
    async function getAddress(dealerId : string, addressId: string) {

        const serverHoursGrouped : IGroupedHours[] = [];
        const svc = new TransportationSvc();

        const response = await svc.getAddress(dealerId, addressId);
        if (response.status === StatusCode.Success) {

            const datas = response.data;
            datas.deliveryHours.forEach((serverItem : DeliveryHourView) => {

                if (serverHoursGrouped.length === 0) {
                    const tmpDHV = new DeliveryHourView();
                    tmpDHV.startTime = serverItem.startTime;
                    tmpDHV.endTime = serverItem.endTime;
                    tmpDHV.uniqueID = uuid();
                    tmpDHV.weekDays = serverItem.weekDays;

                    serverItem.uniqueID = uuid();

                    serverHoursGrouped.push({
                        id: uuid(),
                        items: [tmpDHV],
                        days: serverItem.weekDays
                    });
                    return;
                }

                let foundMatch = false;
                let outerElement;
                for (let j = 0; j < serverHoursGrouped.length; j++) {

                    outerElement = serverHoursGrouped[j];
                    if (Array.isArray(outerElement.items)) {

                        for (let i = 0; i < outerElement.items.length; i++) {
                            const innerElement = outerElement.items[i];
                            if (innerElement.startTime === serverItem.startTime &&
                                innerElement.endTime === serverItem.endTime) {
                                    foundMatch = true;
                                    break;
                            }
                        }                
                    }

                    if (foundMatch) {
                        break;
                    }
                }

                serverItem.uniqueID = uuid();
                if (foundMatch && outerElement) {
                    outerElement.items.push(serverItem);
                }
                else {
                    const tmpDHV = new DeliveryHourView();
                    tmpDHV.startTime = serverItem.startTime;
                    tmpDHV.endTime = serverItem.endTime;
                    tmpDHV.uniqueID = uuid();
                    tmpDHV.weekDays = serverItem.weekDays;

                    serverHoursGrouped.push({
                        id: uuid(),
                        items: [tmpDHV],
                        days: serverItem.weekDays
                    });
                }
            });

            if ((datas.deliveryHours && datas.deliveryHours.length > 0) &&
                (datas.deliveryHours[0].startTime === datas.deliveryHours[0].endTime)) {
                setFixedRangeMode(true);
            }

            setGroupedHours(serverHoursGrouped);
            setEditAddress(datas);
            var deliveryTypes = getRemainingDeliveryTypes(datas);
            setDeliveryTypes(deliveryTypes);
        }
    }

    /**
     * Fetch the address
     */
    React.useEffect(() => {
        const addressId = props.match.params.addressId;
        const dealerId = props.match.params.id;
        
        if (!addressId) {
            const newAddress = new ShipToAddressRead();
            newAddress.customerId = dealerId;
            setIsNewAddressEntry(true);
            makeDefault();
            setEditAddress(newAddress);
            var deliveryTypes = getRemainingDeliveryTypes(newAddress);
            setDeliveryTypes(deliveryTypes);

            const createPerm = Permissions.SupportDealersEdit;

            if (hasPermission({ requiredPermissions: [createPerm] })) {
                setCanEdit(true);
            }
            return;
        }

        getAddress(dealerId, addressId);

        const editPerm = Permissions.SupportDealersEdit;
            
         if (hasPermission({ requiredPermissions: [editPerm]})) {
             setCanEdit(true);
         }

    }, [props.location.state]);

    function getRemainingDeliveryTypes(address: ShipToAddressRead) {

        var remainingTypes = new Array<SelectListItem<string>>();
        for (let type of AddressDeliveryTypeSelectList) {
            if (!address.deliveryTypes.includes(Number(type.id))) {
                remainingTypes.push(type);
            }
        }
        return remainingTypes;
    }

    /**
     * Change event handler for the EditAddressHoursControl
     * @param groupsForControl Grouped hours from a control
     */
    const updateHours = (controlId: string, groupsForControl : DeliveryHourView[], selectedDays : number, forceClear : boolean = false) => {
        
        const tmpAddress                        = {...editAddress};
        const toBeAdded : DeliveryHourView[]    = [];
        let tmpHoursByControl : any             = hoursByControl;      

        if (forceClear) {
            tmpHoursByControl = {};
        }

        // If we don't have record of this control, add an entry
        if (!tmpHoursByControl.hasOwnProperty(controlId)) {            
            tmpHoursByControl[controlId] = [];
        }
        
        tmpHoursByControl[controlId] = groupsForControl;

        Object.keys(tmpHoursByControl).forEach((blahKey : string) => {

            const entry : DeliveryHourView[] = tmpHoursByControl[blahKey];
            entry.forEach((itm : DeliveryHourView) => {

                let hasMatch = false;

                for (let i = 0; i < toBeAdded.length; i++) {
                    const existing = toBeAdded[i];
                    if (itm.startTime === existing.startTime && 
                        (itm.endTime === existing.endTime) && 
                        (itm.weekDays === existing.weekDays)) {
                            hasMatch = true;
                            break;
                    }    
                }

                if (!hasMatch) {
                    const newItem = new DeliveryHourView();
                    newItem.endTime = itm.endTime;
                    newItem.startTime = itm.startTime;
                    newItem.weekDays = itm.weekDays;
                    toBeAdded.push(newItem);
                }
            });
        });

        tmpAddress.deliveryHours = toBeAdded;

        setEditAddress(tmpAddress);

        let tmpGroupedHours = [...groupedHours];
        if (forceClear) {
            tmpGroupedHours = [{
                id: controlId,
                items: [],
                days: 1
            }];
        }

        tmpGroupedHours.forEach((itm : any) => {
            if (itm.id === controlId) {
                itm.items = groupsForControl;

                if (groupsForControl && groupsForControl.length === 0) {
                    itm.days = 0;
                }
                else {
                    itm.days = groupsForControl[0].weekDays;
                }
            }
        });

        setGroupedHours(tmpGroupedHours);
    };

    /**
     * Remove the provided hours
     */
    const removeHours = (idx: string) => {
        
        const tmpHours = [...groupedHours];
        const updated = tmpHours.reduce((acc : any[], curr : any) => {
            if (curr.id !== idx) {
                acc.push(curr);
            }
            return acc;
        }, []);

        // Update the grouped hours
        let adds : DeliveryHourView[] = [];
        updated.forEach((itm : any) => {
            adds = adds.concat(itm.items);
        });

        setGroupedHours(updated);
        
        // Update the address
        const tmpAddress = editAddress;
        tmpAddress.deliveryHours = adds;
        setEditAddress(tmpAddress);

        // Update the hours by control
        const tmpControlHours : any = hoursByControl;
        delete tmpControlHours[idx];
        setHoursByControl(tmpControlHours);

        // Update the grouped hours display
        const all = AppUtils.groupHoursByDayOfWeek(editAddress.deliveryHours);
        setDisplayHours(all);

    };

    /**
     * Add a new hour control
     */
    const addHours = () => {
        const tmpHours = [...groupedHours];
        tmpHours.push({
            id: uuid(),
            items: [],
            days: 1
        });

        setGroupedHours(tmpHours);
    };

    const updateDisplayHours = () => {
        const all = AppUtils.groupHoursByDayOfWeek(editAddress.deliveryHours);
        setDisplayHours(all);
    };

    /**
     * Save the changes via the transportation service
     */
    const onSave = async () => {

        const editVM            = new DeliveryAddressUpdate();
        editVM.latitude         = editAddress.address.latitude;
        editVM.longitude        = editAddress.address.longitude;
        editVM.timeZoneId       = editAddress.address.timeZoneId;
        editVM.rowVersion       = editAddress.rowVersion;
        editVM.unloadRate       = editAddress.unloadRate;
        editVM.country          = editAddress.address.country;
        editVM.phoneNumber      = editAddress.phoneNumber;
        editVM.contactEmailBusiness     = editAddress.contactEmailBusiness;
        editVM.contactNameBusiness      = editAddress.contactNameBusiness;
        editVM.contactPhoneNumberBusiness = editAddress.contactPhoneNumberBusiness;
        editVM.contactEmailSite = editAddress.contactEmailSite;
        editVM.contactNameSite = editAddress.contactNameSite;
        editVM.contactPhoneNumberSite = editAddress.contactPhoneNumberSite;
        editVM.note             = editAddress.note;
        editVM.isJobSite = editAddress.isJobSite;
        editVM.deliveryTypes = editAddress.deliveryTypes;

        editAddress.deliveryHours.forEach((itm : DeliveryHourView) => {

            const hour      = new DeliveryHourUpdate();
            hour.weekDays   = itm.weekDays;
            hour.startTime  = itm.startTime;
            hour.endTime    = itm.endTime;

            editVM.deliveryHours.push(hour);
        });

        setPageErrorMsg('');
        const passed = _validateData(editVM);
        if (passed) {
            const transportationSvc = new TransportationSvc();
            const response = await transportationSvc.updateDeliveryAddress(editAddress.id, editVM);

            if (response.status === StatusCode.Success) {
                history.goBack();
            }
        }
    };

    /**
     * Create address via the transportation service
     */
    const onCreate = async () => {

        const createVM                  = new DeliveryAddressCreate();
        createVM.customerId             = editAddress.customerId;
        createVM.name                   = editAddress.name;
        createVM.phoneNumber            = editAddress.phoneNumber;

        createVM.address.addressLine1   = editAddress.address.addressLine1;
        createVM.address.addressLine2   = editAddress.address.addressLine2 || null;
        createVM.address.city           = editAddress.address.city;
        createVM.address.stateProvince  = editAddress.address.stateProvince;
        createVM.address.postalCode     = editAddress.address.postalCode;
        createVM.address.country        = editAddress.address.country;

        createVM.address.latitude       = editAddress.address.latitude;
        createVM.address.longitude      = editAddress.address.longitude;
        createVM.address.timeZoneId     = editAddress.address.timeZoneId || null;
        createVM.unloadRate             = editAddress.unloadRate;
        createVM.contactEmailBusiness           = editAddress.contactEmailBusiness;
        createVM.contactNameBusiness            = editAddress.contactNameBusiness;
        createVM.contactPhoneNumberBusiness     = editAddress.contactPhoneNumberBusiness;
        createVM.contactEmailSite           = editAddress.contactEmailSite;
        createVM.contactNameSite            = editAddress.contactNameSite;
        createVM.contactPhoneNumberSite     = editAddress.contactPhoneNumberSite;
        createVM.note = editAddress.note;
        createVM.isJobSite = editAddress.isJobSite;
        createVM.deliveryTypes = editAddress.deliveryTypes;

        editAddress.deliveryHours.forEach((itm : DeliveryHourCreate) => {
            const hour      = new DeliveryHourCreate();
            hour.weekDays   = itm.weekDays;
            hour.startTime  = itm.startTime;
            hour.endTime    = itm.endTime;

            createVM.deliveryHours.push(hour);
        });

        setPageErrorMsg('');
        const passed = _validateCreateData(createVM);
        if (!passed) {
            return;
        }

        const transportationSvc = new TransportationSvc();
        let response = await transportationSvc.createDeliveryAddress(createVM.customerId, createVM);
        if (response.status !== StatusCode.Success) {
            return;
        }

        let data = response.data;
        if (data.status === AddressCreateStatus.Created) {
            navigateToPage(`/support/dealers/${createVM.customerId}/addresses`);
            return;
        }

        if (data.status === AddressCreateStatus.NotCreatedGoogleHasSuggestion) {
            const useGoogleAddress = await showSuggestedAddressConfirmation(createVM.address, data.suggestedAddress);
            if (useGoogleAddress) {
                createVM.address = data.suggestedAddress;
                createVM.force = true;
            }
            else if (useGoogleAddress === false) {
                createVM.force = true;
            }
            else {
                // User cancelled the confirmation
                return;
            }

        }
        else if (data.status === AddressCreateStatus.NotCreatedGoogleNotFound) {
            const result = await showAddressConfirmation(createVM.address);
            if (!result) {
                return;
            }
            createVM.force = true;
        }
        else if (data.status === AddressCreateStatus.CannotCreate && data.errorMessage != null) {
            setPageErrorMsg(data.errorMessage);
            return;
        }
        else {
            setPageErrorMsg('Error occurred while creating the address. Please try again.');
            return;
        }

        response = await transportationSvc.createDeliveryAddress(createVM.customerId, createVM);
        if (response.status === StatusCode.Success && response.data.status === AddressCreateStatus.Created) {
            navigateToPage(`/support/dealers/${createVM.customerId}/addresses`);
        }
    };

    /**
     * Prompt the user to confirm whether to use the provided address even if google could not find a matching address.
     * @param address The original entered address
     */
    const showAddressConfirmation = async (address: CreateAddress) => {
        const message = (
            <>
                <div style={{ textAlign: 'center', marginTop: 20, marginBottom: 20 }}>
                    Google could not find a matching address. Do you want to continue using the address you entered?
                </div>

                <div style={{ textAlign: 'left' }}>
                    <strong>Address:</strong>
                    {formatAddress(address)}
                </div>
            </>
        );

        const result = await ResultDialog.show({
            title: 'Address Confirmation',
            messageContent: message,
            affirmativeLabel: 'Yes',
            negativeLabel: 'Cancel'
        });

        return result;
    };

    const formatAddress = (addr: CreateAddress) => (
        <div>
            <div>{addr.addressLine1}</div>
            {addr.addressLine2 && <div>{addr.addressLine2}</div>}
            <div>
                {addr.city}{addr.city && addr.stateProvince ? ', ' : ''}{addr.stateProvince} {addr.postalCode}
            </div>
            <div>{addr.country}</div>
            <div>Latitude: {addr.latitude}</div>
            <div>Longitude: {addr.longitude}</div>
            <div>TimeZoneId: {addr.timeZoneId}</div>
        </div>
    );

    /**
     * Validate the current address to determine if it can be created
     * @param createVM The address model to validate
     */
    const _validateCreateData = (createVM: DeliveryAddressCreate) => {
        const defaultErrorMsg = "This field is required.";
        const msgs = ModelValidator.validate(createVM);
        const errors = { ...msgs };

        if (AppUtils.IsEmpty(createVM.name)) {
            errors.name = [defaultErrorMsg];
        }
        if (AppUtils.IsEmpty(createVM.address.addressLine1)) {
            errors.addressLine1 = [defaultErrorMsg];
        }
        if (AppUtils.IsEmpty(createVM.address.city)) {
            errors.city = [defaultErrorMsg];
        }
        if (AppUtils.IsEmpty(createVM.address.stateProvince)) {
            errors.stateProvince = [defaultErrorMsg];
        }
        if (AppUtils.IsEmpty(createVM.address.postalCode)) {
            errors.postalCode = [defaultErrorMsg];
        }
        if (AppUtils.IsEmpty(createVM.address.country)) {
            errors.country = [defaultErrorMsg];
        }

        if (createVM.isJobSite &&
            (!createVM.contactNameBusiness?.trim()
                || !createVM.contactEmailBusiness?.trim()
                || !createVM.contactPhoneNumberBusiness?.trim()
                || !createVM.contactEmailSite?.trim()
                || !createVM.contactNameSite?.trim()
                || !createVM.contactPhoneNumberSite?.trim())) {
            errors.jobSiteData = true;
        }

        if (!AppUtils.IsEmpty(errors)) {
            setErrorMsgs(errors);
            setPageErrorMsg('One or more required fields are missing. Please fill in all required fields and try again.');
            return false;
        }

        setErrorMsgs({});
        return true;
    };

    /**
     * Validate the current address to determine if it can
     * be saved
     * @param editVM The address model to validate
     */
    const _validateData = (editVM : DeliveryAddressUpdate) => {

        const msgs = ModelValidator.validate(editVM);
        if (editVM.isJobSite &&
            (!editVM.contactNameBusiness?.trim()
                || !editVM.contactEmailBusiness?.trim()
                || !editVM.contactPhoneNumberBusiness?.trim()
                || !editVM.contactEmailSite?.trim()
                || !editVM.contactNameSite?.trim()
                || !editVM.contactPhoneNumberSite?.trim())) {
            msgs.jobSiteData = true;
        }

        if (!AppUtils.IsEmpty(msgs)) {
            setErrorMsgs(msgs);
            return false;
        }

        setErrorMsgs({});
        return true;
    };

    /**
     * Cancel the changes and reload the whole address
     */
    const onCancel = async () => {
        backToList();
    };

    /**
     * Update the address timezone
     * @param selectedTimezone The selected timezone object
     */
    const onTimeZoneChange = (selectedTimezone : any) => {
        const tmpAddress = {
            ...editAddress,
            address: {
                ...editAddress.address,
                timeZoneId: (selectedTimezone) ? selectedTimezone.id : null
            }            
        };

        setEditAddress(tmpAddress);
    };

    /**
     * Update the address country
     * @param selectedCountry The selected country object
     */
    const onCountryChange = (selectedCountry : any) => {
        const tmpAddress = {
            ...editAddress,
            address: {
                ...editAddress.address,
                country: (selectedCountry) ? (selectedCountry.id ? selectedCountry.id : selectedCountry) : null
            }
        };

        setEditAddress(tmpAddress);
    };

    const onMapClick = (newLatitude : number, newLongitude : number) => {

        if (!canEdit) {
            return;
        }

        const tmpAddress = {
            ...editAddress,
            address: {
                ...editAddress.address,
                latitude: newLatitude,
                longitude: newLongitude
            }   
        };

        setEditAddress(tmpAddress);
    };
    
    /**
     * Update the address latitude
     * @param evt The input event from the textbox
     */
    const onLatitudeChange = (evt : any) => {
        const tmpAddress = {
            ...editAddress,
            address: {
                ...editAddress.address,
                latitude: Number.parseFloat(evt.target.value)
            }   
        };

        setEditAddress(tmpAddress);
    };

    const onPhoneNumberChanged = (evt: any) => {
        const tmpAddress = {
            ...editAddress,
            phoneNumber: evt.target.value.replace(/\D/g, '')
        };

        setEditAddress(tmpAddress);
    };

    const onContactPhoneNumberBusinessChanged = (evt: any) => {
        const tmpAddress = {
            ...editAddress,
            contactPhoneNumberBusiness: evt.target.value.replace(/\D/g, '')
        };

        setEditAddress(tmpAddress);
    };

    const onContactPhoneNumberSiteChanged = (evt: any) => {
        const tmpAddress = {
            ...editAddress,
            contactPhoneNumberSite: evt.target.value.replace(/\D/g, '')
        };

        setEditAddress(tmpAddress);
    };

    const onNameChanged = (evt: any) => {
        const tmpAddress = {
            ...editAddress,
            name: evt.target.value
        };

        setEditAddress(tmpAddress);
    };

    const onAddressLine1Changed = (evt: any) => {
        const tmpAddress = {
            ...editAddress,
            address: {
                ...editAddress.address,
                addressLine1: evt.target.value
            }            
        };

        setEditAddress(tmpAddress);
    };

    const onAddressLine2Changed = (evt: any) => {
        const tmpAddress = {
            ...editAddress,
            address: {
                ...editAddress.address,
                addressLine2: evt.target.value
            }            
        };

        setEditAddress(tmpAddress);
    };

    const onCityChanged = (evt: any) => {
        const tmpAddress = {
            ...editAddress,
            address: {
                ...editAddress.address,
                city: evt.target.value
            }            
        };

        setEditAddress(tmpAddress);
    };

    const onStateChanged = (evt: any) => {
        const tmpAddress = {
            ...editAddress,
            address: {
                ...editAddress.address,
                stateProvince: evt.target.value
            }            
        };

        setEditAddress(tmpAddress);
    };

    const onPostalCodeChanged = (evt: any) => {
        const tmpAddress = {
            ...editAddress,
            address: {
                ...editAddress.address,
                postalCode: evt.target.value
            }            
        };

        setEditAddress(tmpAddress);
    };

    /**
     * Update the address longitude
     * @param evt The input event from the textbox
     */
    const onLongitudeChange = (evt : any) => {
        const tmpAddress = {
            ...editAddress,
            address: {
                ...editAddress.address,
                longitude: Number.parseFloat(evt.target.value)
            }            
        };

        setEditAddress(tmpAddress);
    };

    /**
     * Update the address unload rate
     * @param evt The input event from the textbox
     */
    const onUnloadRateChange = (evt : any) => {
        const tmpAddress = {
            ...editAddress,
            unloadRate: evt.target.value
        };

        setEditAddress(tmpAddress);
    };

    /**
     * Navigate back to the list view.
     * We avoid calling go back because we won't necessarily know
     * where to go.
     */
    const backToList = () => {
        navigateToPage(`/support/dealers/${ editAddress.customerId }/addresses`);
    };

    /**
     * Reset the ranged hours to be M-F 8-5
     */
    const makeDefault = () => {

        const cId = uuid();
        setGroupedHours([{
            id: cId,
            items: [],
            days: 1
        }]);

        setHoursByControl({});

        const newView = new DeliveryHourView();
        newView.startTime = "08:00:00";
        newView.endTime = "17:00:00";
        newView.uniqueID = uuid();
        newView.weekDays = 62;

        updateHours(cId, [newView], 62, true);

    };

    const getApprovalDataDisplay = () => {
        if (!editAddress?.approvalDate || !editAddress?.approvalUser) {
            return null;
        }

        const approvalDate = new Date(editAddress.approvalDate);
        const approvalDateDisplay = approvalDate.toLocaleString();
        return (
            <div className="approval-data" style={{ marginBottom: 12 }}>
                <strong>Approved by:</strong> {editAddress.approvalUser} on {approvalDateDisplay}
            </div>
        );
    };

    const selectNewDeliveryType = (key: string) => {
        var numVal = Number(key);
        var typeVal = numVal as AddressDeliveryType;
        if (typeVal) {
            setNewDeliveryType(typeVal);
        }
        else {
            setNewDeliveryType(undefined);
        }
    };

    const addDeliveryType = () => {
        const tmpAddress = { ...editAddress };
        if (newDeliveryType) {
            tmpAddress.deliveryTypes.push(newDeliveryType);
            setEditAddress(tmpAddress);
        }

        var deliveryTypes = getRemainingDeliveryTypes(tmpAddress);
        setDeliveryTypes(deliveryTypes);
        setNewDeliveryType(undefined);
    };

    const removeDeliveryType = (idx: number) => {
        const tmpAddress = { ...editAddress };
        tmpAddress.deliveryTypes.splice(idx, 1);
        setEditAddress(tmpAddress);

        var deliveryTypes = getRemainingDeliveryTypes(tmpAddress);
        setDeliveryTypes(deliveryTypes);
    }

    return editAddress && groupedHours
        ? <PageContent classes="is-in-tabs" pageName={ isNewAddressEntry ? "Create Address" : "Edit Address" }>
            <PageHeading
                shouldStick={ true }
                showBackBtn={ true }
                onBackClick={ backToList }
                title= { isNewAddressEntry ? "Create Address" : "Edit Address" }>
                <PageActions
                        id="edit-address-form"
                        onEdit={ isNewAddressEntry ? onCreate : onSave }
                        editLabel= { isNewAddressEntry ? "Create" : "Save" }
                        editPermissions={[
                            Permissions.SupportDealersEdit
                        ]} 
                        onDelete={ onCancel }
                        deleteLabel="Cancel"
                        deletePermissions={[
                            Permissions.SupportDealersEdit
                        ]} 
                />   
            </PageHeading>
            <PageBody classes='edit-delivery-address'>
                {pageErrorMsg ? <PageLevelError message={pageErrorMsg} /> : null}
                {
                    errorMsgs.Address
                        ? <div className="row">
                                <div className="col">
                                <span key="itemserror" className='page-action-error fixed-mode'>
                                    {errorMsgs.Address}
                                </span>
                            </div>
                        </div> 
                        : null
                }

                {getApprovalDataDisplay()}

                {
                    isNewAddressEntry ?
                        <AddressEntryDisplay
                            name={ editAddress.name }
                            addressLine1={ editAddress.address.addressLine1 }
                            addressLine2={ editAddress.address.addressLine2 }
                            city={ editAddress.address.city }
                            stateProvince={ editAddress.address.stateProvince }
                            postalCode={ editAddress.address.postalCode }
                            phoneNumber={ editAddress.phoneNumber }
                            longitude={ editAddress.address.longitude }
                            latitude={ editAddress.address.latitude }
                            onClick={ onMapClick }
                            phoneNumberChanged={ onPhoneNumberChanged }
                            nameChanged={ onNameChanged }
                            addressLine1Changed={ onAddressLine1Changed }
                            addressLine2Changed={ onAddressLine2Changed }
                            cityChanged={ onCityChanged }
                            stateChanged={ onStateChanged }
                            postalCodeChanged={ onPostalCodeChanged }
                            errorMsgs={ errorMsgs }
                        />
                        :
                        <AddressDisplay
                            label="Delivery Address"
                            name={ editAddress.name }
                            addressLine1={ editAddress.address.addressLine1 }
                            addressLine2={ editAddress.address.addressLine2 }
                            city={ editAddress.address.city }
                            state={ editAddress.address.stateProvince }
                            postalCode={ editAddress.address.postalCode }
                            phoneNumber={ editAddress.phoneNumber }
                            longitude={ editAddress.address.longitude }
                            latitude={ editAddress.address.latitude }
                            onClick={ onMapClick }
                            canEditPhoneNumber={ true }
                            phoneNumberChanged={ onPhoneNumberChanged }
                        />
                }
                  
                <div className="row">
                    <div className="col-md-6">
                        <TextInput
                            type="number"
                            id="latitude"
                            label="Latitude"
                            error={ errorMsgs.latitude }
                            disable={ !canEdit }
                            onChange={ onLatitudeChange }
                            value={ editAddress.address.latitude } />
                    </div>
                    <div className="col-md-6">
                        <TimeZoneInput
                            id={ 'address-timezone' }
                            label={ 'Timezone' }
                            disable={ !canEdit }
                            error={ errorMsgs.timeZoneId }
                            onChange={ onTimeZoneChange }
                            value={ editAddress.address.timeZoneId }
                        />           
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6">
                         <TextInput
                            type="number"
                            id="longitude"
                            label="Longitude"
                            error={ errorMsgs.longitude }
                            disable={ !canEdit }
                            onChange={ onLongitudeChange }
                            value={ editAddress.address.longitude } />                
                    </div>
                    <div className="col-md-6">
                         <CountryInput
                            id={ 'address-country' }
                            label={ 'Country' }
                            disable={ !canEdit }
                            error={ errorMsgs.country }
                            onChange={ onCountryChange }
                            value={ editAddress.address.country } 
                        />              
                    </div>
                </div>    

                <div className="row">
                    <div className="col-md-6">
                        <TextInput
                            type="number"
                            id="unloadRate"
                            label="Unload Rate (units/hr)"
                            value={ editAddress.unloadRate }
                            disable={ !canEdit }
                            onChange={ onUnloadRateChange }
                        />             
                    </div>
                    <div className="col-md-6">
                        <div className="form-group">
                            <label className="label undefined" htmlFor="approvedDeliveryTypes">Approved Delivery Types</label>
                            <table className="multi-select-table">
                                <tbody>
                                    {
                                        editAddress.deliveryTypes.map((dt, index) => (
                                            <tr key={index}>
                                                <td>{AddressDeliveryTypeMetadata[dt].display}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn-icon remove-btn"
                                                        disabled={ !canEdit }
                                                        onClick={() => removeDeliveryType(index)}>
                                                        <i className="la la-times" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    <tr>
                                        <td>
                                            <select
                                                id="newDeliveryType"
                                                className="form-control"
                                                disabled={ !canEdit }
                                                value={newDeliveryType}
                                                onChange={(e) => selectNewDeliveryType(e.target.value)}>
                                                <option value="">Select a delivery type</option>
                                                {deliveryTypes.map((dt) => (
                                                    <option key={dt.id} value={dt.id}>
                                                        {dt.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            <button
                                                id="add-delivery-type"
                                                onClick={addDeliveryType}
                                                type="button"
                                                className="btn-icon add-btn"
                                                disabled={ !canEdit }>
                                                Add
                                                <i className="la la-plus" />
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {
                    errorMsgs.jobSiteData
                        ? <div className="row">
                                <div className="col">
                                <span key="itemserror" className='page-action-error fixed-mode'>
                                    Contact name, email, and phone number for both the business and site are required for job sites.
                                </span>
                            </div>
                        </div> 
                        : null
                }

                <div className="row" style={{ marginTop: 12 }}>
                    <div className="col-md-12">
                        <ToggleInput
                            id="is-job-site"
                            name="is-job-site"
                            label="Is Job Site"
                            disable={ !canEdit }
                            checked={ editAddress.isJobSite }
                            onClick={() => setEditAddress({...editAddress, isJobSite: !editAddress.isJobSite })}
                        />
                    </div>
                </div>
                
                <div className="row">
                    <div className="col-md-6">
                        <TextInput
                            type="text"
                            id="contact-name-business"
                            label={editAddress.isJobSite ? 'Contact Name(Business)*' : 'Contact Name(Business)' }
                            disable={ !canEdit }
                            value={editAddress.contactNameBusiness}
                            onChange={(e: any) => setEditAddress({ ...editAddress, contactNameBusiness: e.target.value })}
                        />
                    </div>

                    <div className="col-md-6">
                        <TextInput
                            type="text"
                            id="contact-name-site"
                            label={editAddress.isJobSite ? 'Contact Name(Site)*' : 'Contact Name(Site)'}
                            disable={ !canEdit }
                            value={editAddress.contactNameSite}
                            onChange={(e: any) => setEditAddress({ ...editAddress, contactNameSite: e.target.value })}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6">
                        <TextInput
                            type="text"
                            id="contact-email-business"
                            label={editAddress.isJobSite ? 'Contact Email(Business)*' : 'Contact Email(Business)'}
                            disable={ !canEdit }
                            value={editAddress.contactEmailBusiness}
                            onChange={(e: any) => setEditAddress({ ...editAddress, contactEmailBusiness: e.target.value })}
                        />
                    </div>
                    <div className="col-md-6">
                        <TextInput
                            type="text"
                            id="contact-email-site"
                            label={editAddress.isJobSite ? 'Contact Email(Site)*' : 'Contact Email(Site)'}
                            disable={ !canEdit }
                            value={editAddress.contactEmailSite}
                            onChange={(e: any) => setEditAddress({ ...editAddress, contactEmailSite: e.target.value })}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6">
                        <TextInput
                            type="text"
                            id="contact-phone-business"
                            label={ editAddress.isJobSite ? 'Contact Phone Number(Business)*' : 'Contact Phone Number(Business)' }
                            disable={ !canEdit }
                            value={`tel: ${AppUtils.formatPhone(editAddress.contactPhoneNumberBusiness ?? '')}`}
                            onChange={ onContactPhoneNumberBusinessChanged }
                        />
                    </div>
                    <div className="col-md-6">
                        <TextInput
                            type="text"
                            id="contact-phone-site"
                            label={editAddress.isJobSite ? 'Contact Phone Number(Site)*' : 'Contact Phone Number(Site)'}
                            disable={ !canEdit }
                            value={`tel: ${AppUtils.formatPhone(editAddress.contactPhoneNumberSite ?? '')}`}
                            onChange={onContactPhoneNumberSiteChanged}
                        />
                    </div>

                </div>

                <div className="row" style={{ marginTop: 12 }}>
                    <div className="col-md-12">
                        <TextArea
                            id="address-note"
                            label="Note"
                            disable={ !canEdit }
                            value={editAddress.note}
                            onChange={(e: any) => setEditAddress({ ...editAddress, note: e.target.value })}
                            defaultLines={4}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col">
                        <PageActionHeading
                            id="addlineitem"
                            heading={
                                <div className="hour-subheading">
                                    <span>Hours</span>
                                    {
                                        !fixedRangeMode && canEdit
                                            ? <button
                                                className="btn-clear" 
                                                onClick={ makeDefault }
                                            >
                                                (Set to Default)
                                            </button>
                                            : null
                                    }
                                </div>
                            }
                            actionText="Add"
                            actionIcon="la-plus-circle"
                            actionDisable={ !canEdit }
                            actionClick={ addHours } />
                    </div>
                </div>
                
                <div className="row">
                    <div className="col">
                        <div className="form-check fixed-range-check">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                checked={ fixedRangeMode } 
                                disabled={ !canEdit }
                                onChange={ () => setFixedRangeMode(!fixedRangeMode)}
                                id="fixed-hours" />
                            <label className="form-check-label" htmlFor="fixed-hours">
                                Fixed Range Mode?
                            </label>
                        </div>                        
                    </div>
                </div>

                {
                    errorMsgs.deliveryHours
                        ? <div className="row">
                                <div className="col">
                                <span key="itemserror" className='page-action-error fixed-mode'>
                                    You must provide at least one set of hours
                                </span>
                            </div>
                        </div> 
                        : null
                }

                {
                    groupedHours && groupedHours.length > 0
                        ? groupedHours.map((itm : IGroupedHours, idx: number) => (
                            <EditAddressHoursControl 
                                key={ itm.id }
                                id={ itm.id } 
                                deliveryHours={ itm.items }
                                deliveryDays={ itm.days } 
                                disabled={ !canEdit }
                                onUpdate={ updateHours } 
                                onRemove={ removeHours }
                                showFixedRange= { fixedRangeMode }
                                onDisplayUpdate={ updateDisplayHours }
                            />
                        ))
                        : <div className="empty-placeholder-div">
                            <h2>Click the add button to get started</h2>
                        </div>
                }
                                
                {
                    groupedHours && groupedHours.length > 0
                        ? <React.Fragment>
                            <div className="row">
                                <div className="col">
                                    <PageActionHeading
                                        id="hours-display"
                                        heading="Hour Summary" />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col">
                                    
                                    <div className="display-hours-row">
                                    {
                                        displayHours && Object.keys(displayHours).length > 0
                                            ? Object.keys(displayHours).map((item : string, outerIdx: number) => 
                                            
                                                displayHours[item].length > 0
                                                    ? <div className="display-hour" key={`outer-group-${outerIdx}`}>
                                                        <p className="day-label">
                                                            { WeekDaysMetadata[Number.parseInt(item, 10) as WeekDays].display }
                                                        </p>
                                                        {
                                                            displayHours[item].map((itm : any, innerIdx: number) => 
                                                                <p 
                                                                    className="inner-hour" 
                                                                    key={`inner-hour-${innerIdx}`}>
                                                                        {
                                                                            fixedRangeMode
                                                                                ? itm.start
                                                                                : `${ itm.start } to ${ itm.end }`
                                                                        }
                                                                </p>
                                                            )
                                                        }                                            
                                                    </div>
                                                    : null
                                            )
                                            : <div className="empty-placeholder-div">
                                                <h2>Please set some hours to review the summary.</h2>
                                            </div>
                                    }
                                    </div>
                                </div>
                            </div>
                        </React.Fragment> 
                        : null
                }

                <div className="row">
                    <div className="col">
                       
                    </div>
                </div>

                {
                    IS_DEBUG
                        ? <pre>
                            {
                                JSON.stringify(editAddress.deliveryHours, null, 2)
                            }
                        </pre> 
                        : null
                }

            </PageBody>
        </PageContent> 
        : null;
    
};
